/* Environment Map — SVG MapNode and MapEdge components */
import { Star } from '@phosphor-icons/react';
import { HEALTH_COLORS, EDGE_COLORS, CATEGORY_ICONS } from './constants';

export function MapNode({ node, x, y, isSelected, isAffected, isDimmed, isSearchHit, onClick, onDragStart, simplified }) {
  var healthColor = HEALTH_COLORS[node.health_status] || HEALTH_COLORS.unknown;

  if (simplified) {
    var sColor = isSelected ? '#0ABFA3' : isAffected ? '#E24B4A' : node.is_critical_path ? '#FFD700' : healthColor;
    return (
      <g transform={'translate(' + x + ',' + y + ')'} style={{ cursor: 'pointer', opacity: isDimmed ? 0.15 : 1 }} data-testid={'map-node-' + node.node_id}
         onClick={function (e) { e.stopPropagation(); onClick(node); }}>
        <circle r={10} fill="#0C1220" stroke={sColor} strokeWidth={isSelected ? 2 : 1} />
        <circle r={3} fill={healthColor} />
        <title>{node.label}</title>
      </g>
    );
  }

  var catIcon = CATEGORY_ICONS[node.category] || 'K';
  var opacity = isDimmed ? 0.15 : 1;
  var isCritical = node.is_critical_path;
  var borderColor = isSelected ? '#0ABFA3' : isAffected ? '#E24B4A' : isCritical ? '#FFD700' : isSearchHit ? '#4285F4' : 'rgba(10,191,163,0.12)';
  var strokeW = isSelected || isAffected || isCritical ? 2 : 1;

  return (
    <g transform={'translate(' + x + ',' + y + ')'} style={{ cursor: 'pointer', opacity: opacity }} data-testid={'map-node-' + node.node_id}
       onClick={function (e) { e.stopPropagation(); onClick(node); }}
       onMouseDown={function (e) { if (onDragStart) onDragStart(e, node); }}
    >
      <rect x={-60} y={-24} width={120} height={48} rx={4} fill="#0C1220" stroke={borderColor} strokeWidth={strokeW} />
      {node.has_breaking_drift && <rect x={-60} y={-24} width={120} height={48} rx={4} fill="none" stroke="#F4A030" strokeWidth={1} strokeDasharray="4 2" />}
      {isCritical && <Star size={10} weight="fill" x={46} y={-20} style={{ color: '#FFD700' }} />}
      <circle cx={-46} cy={-10} r={3} fill={healthColor} />
      <text x={-38} y={-6} fill="#7A9AB0" fontSize="10" fontFamily="monospace" textAnchor="start">
        {node.label.length > 14 ? node.label.slice(0, 13) + '\u2026' : node.label}
      </text>
      <text x={-46} y={10} fill="#4A6077" fontSize="8" fontFamily="monospace">{node.provider || node.node_type}</text>
      <text x={42} y={-6} fill="rgba(10,191,163,0.12)" fontSize="14" fontFamily="monospace" fontWeight="bold" textAnchor="end">{catIcon}</text>
      {isCritical && <text x={-46} y={20} fill="#FFD700" fontSize="7" fontFamily="monospace">CRITICAL PATH</text>}
    </g>
  );
}

export function MapEdge({ x1, y1, x2, y2, edge, isAffected, isSelected, onClick, simplified }) {
  var color = isAffected ? '#E24B4A' : isSelected ? '#0ABFA3' : (EDGE_COLORS[edge.edge_type] || 'rgba(10,191,163,0.12)');
  if (edge.error_rate_7d > 0.1) color = '#E24B4A';
  else if (edge.error_rate_7d > 0.05) color = '#C8A030';
  var width = isSelected ? 2.5 : edge.criticality === 'critical' ? 2 : 1;

  var dx = x2 - x1, dy = y2 - y1;
  var len = Math.sqrt(dx * dx + dy * dy) || 1;
  var ux = dx / len, uy = dy / len;
  var sx = x1 + ux * 64, sy = y1 + uy * 28;
  var ex = x2 - ux * 64, ey = y2 - uy * 28;

  var isAnimated = edge.is_active && edge.volume_daily > 10;
  var dash = edge.is_active ? (isAnimated ? '8 4' : 'none') : '6 3';

  if (simplified) {
    return <line x1={sx} y1={sy} x2={ex} y2={ey} stroke={color} strokeWidth={1} opacity={0.4} />;
  }

  return (
    <g data-testid={'map-edge-' + edge.edge_id} style={{ cursor: 'pointer' }} onClick={function (e) { e.stopPropagation(); if (onClick) onClick(edge); }}>
      <line x1={sx} y1={sy} x2={ex} y2={ey} stroke="transparent" strokeWidth={10} />
      <line x1={sx} y1={sy} x2={ex} y2={ey} stroke={color} strokeWidth={width} strokeDasharray={dash} opacity={0.7}>
        {isAnimated && (
          <animate attributeName="stroke-dashoffset" from="24" to="0" dur={edge.volume_daily > 100 ? '0.4s' : edge.volume_daily > 50 ? '0.7s' : '1.2s'} repeatCount="indefinite" />
        )}
      </line>
      <polygon points={[(ex) + ',' + (ey), (ex - ux * 8 + uy * 4) + ',' + (ey - uy * 8 - ux * 4), (ex - ux * 8 - uy * 4) + ',' + (ey - uy * 8 + ux * 4)].join(' ')} fill={color} opacity={0.7} />
      {edge.has_transforms && <circle cx={(sx + ex) / 2} cy={(sy + ey) / 2} r={4} fill="#A78BFA" stroke="#0C1220" strokeWidth={1} />}
      {edge.volume_daily > 0 && <text x={(sx + ex) / 2} y={(sy + ey) / 2 - 8} fill="#4A6077" fontSize="8" fontFamily="monospace" textAnchor="middle">{edge.volume_daily}/d</text>}
    </g>
  );
}
